/**
 * Focus an element once, on mount.
 *
 * Returns the ref to attach, rather than taking one, so the call site is a
 * single line and there is no way to forget the effect half.
 *
 * Six places in the app hand-roll this (`ExtrasManager` ×3, `SourceItem`,
 * `FolderFormModal`, `ChatInterface`), and they disagree on one detail: some
 * focus synchronously on mount, others wrap it in a `setTimeout(…, 50)`. The
 * delay is not superstition — inside a modal that animates in, or an input that
 * mounts in the same commit as a layout change, a synchronous focus can be
 * undone by the browser restoring scroll/focus afterwards. `delayMs` makes that
 * choice explicit instead of implicit in whether someone remembered the wrapper.
 *
 * Only the two `ExtrasManager` call sites are converted here.
 */
import { useEffect, useRef } from 'react';

export function useAutoFocus<T extends HTMLElement>(delayMs = 0) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (delayMs === 0) {
      ref.current?.focus();
      return;
    }
    const t = window.setTimeout(() => ref.current?.focus(), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs]);

  return ref;
}
